const invalidCharsRegex = /[^0-9-+]/;
const oneHyphenRegex = /^(-\d+|\d+-\d+)$/;  // e.g. -x or x-y
const twoHyphenRegex = /^(-\d+-\d+)$/;  // e.g. -x-y
const threeHyphenRegex = /^(-\d+--\d+)$/;  // e.g. -x--y

/**
 * Takes a "FilterValue.value" string and determines if it is formatted correctly for use
 * with the PlugRangeSlider component. Valid formats are those where the string can be
 * converted to a number or a range of numbers, e.g. x or [x, y].
 *
 * @param {string} value
 * @return {boolean}
 */
const valueHasValidFormat = function (value) {
    if (!value.length) {
        return false;
    }

    // If the value contains a character other than numbers, "-", or "+", it is invalid.
    if (invalidCharsRegex.test(value)) {
        return false;
    }

    // If the value contains a "+" and it is not the last character, it is invalid.
    if (
        value.includes("+")
        && value.indexOf("+") !== value.length - 1
    ) {
        return false;
    }

    value = value.replace("+", "");

    // If hyphens are used, there are only three valid formats: -x, -x-y, -x--y.
    if (value.includes("-")) {
        const chars = value.split("");
        const numHyphens = chars.filter(char => char === "-").length;
        if (numHyphens === 1) {
            if (!oneHyphenRegex.test(value)) {
                return false;
            }
        } else if (numHyphens === 2) {
            if (!twoHyphenRegex.test(value)) {
                return false;
            }
        } else if (numHyphens === 3) {
            if (!threeHyphenRegex.test(value)) {
                return false;
            }
        } else {
            return false;
        }
    }

    return true;
};

/**
 * Takes a "FilterValue.value" string and, if possible, returns the numeric value. Otherwise,
 * returns undefined. Examples of valid inputs would be: "1", "1+", "1-5", "-1-5", "-8--1".
 * These would be converted to: 1, 1, [1, 5], [-1, 5], and [-8, -1], respectively.
 *
 * @param {string} value
 * @return {number | [number, number] | undefined | Fraction | [Fraction, Fraction]}
 */
const convertValueToNumberOrRange = function (value) {
    if (!valueHasValidFormat(value)) {
        return undefined;
    }

    // Strip spaces.
    value = value.replace(/\s/g, "");

    // Count hyphens.
    const chars = value.split("");
    const numHyphens = chars.filter(char => char === "-").length;

    let interval;
    if (numHyphens === 0) {
        return Number(value.replace("+", ""));
    } else if (numHyphens === 1) {
        if (value[0] === "-") {
            return Number(value);
        } else {
            interval = value.split("-").map(str => Number(str));
        }
    } else if (numHyphens === 2) {
        const parts = value.slice(1).split("-").map(str => Number(str));
        interval = [-1*parts[0], parts[1]];
    } else {
        // Assert: numHyphens === 3
        const parts = value.split("--").map(str => Number(str));
        interval = [-1*parts[0], -1*parts[1]];
    }
    // Make sure that first value in interval is less than second value.
    if (interval[0] < interval[1]) {
        return interval;
    } else if (interval[0] === interval[1]) {
        return interval[0];
    } else {
        return undefined;
    }
};

class PlugRangeSlider extends PlugFilterSlider {
    constructor() {
        super();
    }

    connectedCallback() {
        this.units = this.dataset.units;
        this.elements = {
            controlStart: this.querySelector(".plug-control-start"),
            controlEnd: this.querySelector(".plug-control-end"),
            activeLine: this.querySelector(".plug-range-slider__active-line"),
            line: this.querySelector(".plug-range-slider__line"),
            lineLabels: Array.from(this.getElementsByClassName("plug-range-slider__line-label")),
            slider: this.querySelector(".plug-range-slider__slider"),
            hiddenInputs: Array.from(this.querySelectorAll(".plug-range-slider__hidden-inputs > input")),
            hiddenStartInput: this.querySelector(`input[name="${this.dataset.metaobjectId}-start"]`),
            hiddenEndInput: this.querySelector(`input[name="${this.dataset.metaobjectId}-end"]`),
        };
        this.controlWidth = 16;
        this.lineWidth = 230;
        this.preInitLinkToReformat = undefined;
        this.currentRangeHasNoResults = false;
        this.isInitialized = false;
        this.init();
        this.isInitialized = true;
        if (this.preInitLinkToReformat) {
            this.reformatActiveFilter(this.preInitLinkToReformat);
        }
        this.removeListeners = this.initListeners();
    }

    init() {
        this.relevantFilterValues = this.filterData.values.filter(value => value.count || value.active);
        /** @type {number | [number, number] | Fraction | [Fraction, Fraction] | undefined} */
        this.numericalValues = this.relevantFilterValues.map(value =>
            convertValueToNumberOrRange(
                (
                    this.units === "Inches" ? this.preProcessInchesValue(value.value)
                    : this.units === "Feet" ? this.preProcessFeetValue(value.value)
                    : this.units === "Days" ? this.preProcessDaysValue(value.value)
                    : value.value
                )
            ));

        if (!this.numericalValues.some(value => value !== undefined)) {
            this.hideParentBlock();
            return;
        }
        this.showParentBlock();

        const [searchParamStartValue, searchParamEndValue] = this.getStateFromSearchParams();

        const [minValue, maxValue] = this.computeMinAndMaxValues(this.numericalValues);
        this.minValue = minValue;
        this.maxValue = maxValue;
        this.totalRange = this.maxValue - this.minValue;

        // The idea here is that if someone adjusts the slider, then re-adjusts it before the page
        // has rendered from the first adjustment, we want to cancel that first page render (keeping
        // in mind that this component will be removed during the render process). The caveat here is
        // that we don't want to delay the page fetch/update process, otherwise we would use a standard
        // debounce approach.
        //
        // Each time the emitChange method is called, we insert a new quitEarly object into the
        // following array and pass the object to the "PLUG.collectionFilters.handleChange" function.
        // When/if emitChange is called again, we set the last quitEarly object to true and add a new
        // one to the array.
        //
        // The "handleChange" function triggers a fetch request (for the sections) followed by the
        // page render process. After the fetch request, but before the page render, it checks the
        // value of the quitEarly object it received. If quitEarly is true, it quits early without
        // rendering anything.

        /** @type {{quitEarly: boolean}[]} */
        this.quitEarlyObjects = [];

        this.setLineLabelValues();

        this.setState(
            searchParamStartValue ? Math.max(this.minValue, Number(searchParamStartValue)) : this.minValue,
            searchParamEndValue ? Math.min(this.maxValue, Number(searchParamEndValue)) : this.maxValue
        );

        if (searchParamStartValue !== null || searchParamEndValue !== null) {
            // Determine if current selection is empty (has no corresponding products).
            const currentRange = [this.currentStartValue, this.currentEndValue];

            // Determine if currentRange overlaps one or more numerical values.
            const hasAtLeastOneResult = this.numericalValues.find(value => {
                if (typeof value === "number") {
                    return (
                        value >= currentRange[0]
                        && value <= currentRange[1]
                    );
                } else if (Array.isArray(value)) {
                    return (
                        (value[0] >= currentRange[0] && value[0] <= currentRange[1])
                        || (value[1] >= currentRange[0] && value[1] <= currentRange[1])
                        || (value[0] <= currentRange[0] && value[1] >= currentRange[1])
                    );
                }
            });

            if (hasAtLeastOneResult === undefined) {
                this.currentRangeHasNoResults = true;
            }
        }

        if (
            this.currentStartValue !== this.minValue
            || this.currentEndValue !== this.maxValue
        ) {
            this.updateFormInputs(this.currentStartValue, this.currentEndValue);
        }
    }

    preProcessDaysValue(value) {
        if (!value.includes("days")) {
            return value;
        } else {
            return value.replace(" days", "").replace("days", "");
        }
    }

    preProcessInchesValue(value) {
        if (!value.includes("in.") && !value.includes("ft.")) {
            return value;
        }
        if (value.includes("in.")) {
            return value.replace(" in.", "").replace(".in", "");
        }
        if (value.includes("ft.")) {
            const numberPart = value.replace(" ft.", "");
            if (numberPart.includes("-")) {
                const numbers = numberPart.split("-");
                return [Number(numbers[0]) * 12, Number(numbers[1]) * 12].join("-");
            } else {
                return String(Number(numberPart) * 12);
            }
        }
    }

    preProcessFeetValue(value) {
        return value.replace(" ft.", "").replace("ft.", "");
    }

    /** @param {HTMLAnchorElement} activeFilterLink */
    reformatActiveFilter(activeFilterLink) {
        if (!this.isInitialized) {
            this.preInitLinkToReformat = activeFilterLink;
            return;
        }

        // compute href
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete(this.dataset.paramName);
        searchParams.delete(`${this.dataset.metaobjectId}-start`);
        searchParams.delete(`${this.dataset.metaobjectId}-end`);
        activeFilterLink.href = `${location.pathname}?${searchParams.toString()}`;

        // compute text content.
        const rangeSpan = activeFilterLink.querySelector(".plug-active-filter-range");
        rangeSpan.textContent = (
            this.formatValueForDisplay(this.currentStartValue) !== this.formatValueForDisplay(this.currentEndValue)
            ? `${this.formatValueForDisplay(this.currentStartValue)} - ${this.formatValueForDisplay(this.currentEndValue)}`
            : this.formatValueForDisplay(this.currentStartValue)
        );
    }

    buildActiveFilter(html = document) {
        const existingActiveFilter = html.querySelector(`.is-plug-slider-filter[data-metaobject-id="${this.dataset.metaobjectId}"]`);
        if (!existingActiveFilter && (this.currentStartValue !== this.minValue || this.currentEndValue !== this.maxValue)) {
            const link = document.getElementById("plug-active-filter-template").content.cloneNode(true).querySelector("a");
            link.dataset.metaobjectId = this.dataset.metaobjectId;
            link.querySelector(".plug-active-filter-label").textContent = `${this.dataset.label}: `;
            this.reformatActiveFilter(link);
            const clearAll = html.querySelector(".active-filters__remove-all");
            if (clearAll) {
                html.querySelector(".plug-collection__active-filters").insertBefore(link, clearAll);
            } else {
                html.querySelector(".plug-collection__active-filters").appendChild(link);
            }
        }
    }

    getStateFromSearchParams() {
        const searchParams = new URLSearchParams(window.location.search);
        return [
            searchParams.get(`${this.dataset.metaobjectId}-start`),
            searchParams.get(`${this.dataset.metaobjectId}-end`)
        ];
    }

    measure() {
        this.controlWidth = this.elements.controlStart.getBoundingClientRect().width;
        this.lineWidth = this.elements.line.getBoundingClientRect().width;
    }

    formatValueForDisplay(value) {
        value = Number(value);
        if (this.units === "Days" || this.units === "Degrees") {
            value = Math.round(value);
        } else if (this.units === "Inches") {
            const inchesInt = Math.round(value);
            const feet = Math.floor(inchesInt / 12);
            const remInches = inchesInt % 12;
            const footPart = (feet || !remInches) ? `${feet}'` : ``;
            const inchesPart = remInches ? `${remInches}"` : ``;
            value = (
                footPart && inchesPart
                ? `${footPart} ${inchesPart}`
                : `${footPart}${inchesPart}`
            );
        } else if (this.units === "Feet") {
            value = `${this.toFixed(value)}'`;
        } else {
            value = this.toFixed(value);
        }
        return value;
    }

    setLineLabelValues() {
        const lineLabels = this.elements.lineLabels;
        const breakpoints = [
            this.minValue,
            this.minValue + ((this.maxValue - this.minValue) / 4),
            this.minValue + ((this.maxValue - this.minValue) / 2),
            this.minValue + (((this.maxValue - this.minValue) / 4) * 3),
            this.maxValue
        ];
        if (this.minValue === this.maxValue) {
            breakpoints.forEach((breakpoint, index) => {
                if (index === 0 || index === breakpoints.length - 1) {
                    lineLabels[index].textContent = this.formatValueForDisplay(breakpoint);
                } else {
                    lineLabels[index].textContent = "";
                }
            });
        } else {
            breakpoints.forEach((breakpoint, index) => {
                lineLabels[index].textContent = this.formatValueForDisplay(breakpoint);
            });
        }
    }

    toFixed(num) {
        const str = num.toFixed(2);
        if (str[str.length - 2] === "0") {
            if (str[str.length - 1] === "0") {
                return str.split(".")[0];
            } else {
                return str.slice(0, str.length - 1);
            }
        }
        return str;
    }

    computeMinAndMaxValues(values) {
        let minValue = Infinity;
        let maxValue = -Infinity;
        values.forEach(value => {
            if (typeof value === "number") {
                minValue = Math.min(value, minValue);
                maxValue = Math.max(value, maxValue);
            } else if (Array.isArray(value)) {
                minValue = Math.min(value[0], minValue);
                maxValue = Math.max(value[1], maxValue);
            }
        });
        return [
            minValue,
            maxValue
        ];
    }

    computeValueUnitPerPx() {
        return this.totalRange / this.lineWidth;
    }

    initListeners() {
        if (this.minValue !== this.maxValue) {
            const removeStartControlListeners = this.initStartControl();
            const removeEndControlListeners = this.initEndControl();
            return () => {
                removeStartControlListeners();
                removeEndControlListeners();
            };
        } else {
            this.classList.add("is-disabled");
        }
    }

    updateFormInputs(startValue, endValue) {
        // The entire slider range being selected implies that this component is not applying any filters.
        if (startValue === this.minValue && endValue === this.maxValue) {
            this.elements.hiddenInputs.forEach(input => {
                input.checked = false;
            });
            this.elements.hiddenStartInput.checked = false;
            this.elements.hiddenEndInput.checked = false;
            return;
        }

        // Find all filter values in the interval [startValue, endValue].
        const activeValues = [];
        this.numericalValues.forEach((value, index) => {
            if (typeof value === "number") {
                if (startValue <= value && value <= endValue) {
                    activeValues.push(this.relevantFilterValues[index]);
                }
            } else if (Array.isArray(value)) {
                if (
                    (value[0] >= startValue && value[0] <= endValue)
                    || (value[1] >= startValue && value[1] <= endValue)
                    || (value[0] <= startValue && value[1] >= endValue)
                ) {
                    activeValues.push(this.relevantFilterValues[index])
                }
            }
        });

        this.elements.hiddenInputs.forEach((input) => {
            const activeValue = activeValues.find(activeValue => (
                activeValue.paramName === input.name
                && activeValue.value === input.value
            ));
            input.checked = !!activeValue;
        });
        this.elements.hiddenStartInput.value = this.toFixed(startValue);
        this.elements.hiddenEndInput.value = this.toFixed(endValue);
        this.elements.hiddenStartInput.checked = true;
        this.elements.hiddenEndInput.checked = true;
    }

    emitChange(startValue, endValue) {
        if (this.quitEarlyObjects.length) {
            this.quitEarlyObjects[this.quitEarlyObjects.length - 1].quitEarly = true;
        }
        this.updateFormInputs(startValue, endValue);
        const parentForm = this.closest("form");
        if (parentForm) {
            this.quitEarlyObjects.push({ quitEarly: false });
            window.PLUG.collectionFilters.handleChange(parentForm, this.quitEarlyObjects[this.quitEarlyObjects.length - 1]);
        } else {
            console.warn("Cannot find parent form.");
        }
    }

    initStartControl() {
        const control = this.elements.controlStart;
        let isDragging = false;
        let valueUnitPerPx = this.computeValueUnitPerPx();
        let dragStartValue;

        const handlePointerUp = () => {
            isDragging = false;
            if (this.currentStartValue !== dragStartValue) {
                this.emitChange(this.currentStartValue, this.currentEndValue);
            }
        };

        /** @param {PointerEvent} e */
        const handlePointerMove = (e) => {
            // movementX: X coordinate delta since last move event.
            if (!isDragging) return;
            if (e.movementY === 0 && e.movementX === 0) {
                return;
            }
            const currentValue = this.currentStartValue;
            const valueDelta = Math.fround(e.movementX * valueUnitPerPx);
            const newValue = Math.min(this.currentEndValue, Math.max(this.minValue, currentValue + valueDelta));
            this.setState(newValue);
        };

        /**
         * @param {PointerEvent} e
         */
        const handlePointerDown = (e) => {
            dragStartValue = this.currentStartValue;
            valueUnitPerPx = this.computeValueUnitPerPx();
            control.setPointerCapture(e.pointerId);
            control.addEventListener("pointerup", handlePointerUp, false);
            control.addEventListener("pointermove", handlePointerMove, false);
            isDragging = true;
        };

        control.addEventListener("pointerdown", handlePointerDown, false);
        control.ondragstart = () => false;

        return () => {
            control.removeEventListener("pointerdown", handlePointerDown, false);
            control.removeEventListener("pointerup", handlePointerUp, false);
            control.removeEventListener("pointermove", handlePointerMove, false);
            control.ondragstart = null;
        };
    }

    initEndControl() {
        const control = this.elements.controlEnd;
        let isDragging = false;
        let valueUnitPerPx = this.computeValueUnitPerPx();
        let dragStartValue;

        const handlePointerUp = () => {
            isDragging = false;
            if (this.currentEndValue !== dragStartValue) {
                this.emitChange(this.currentStartValue, this.currentEndValue);
            }
        };

        /** @param {PointerEvent} e */
        const handlePointerMove = (e) => {
            // movementX: X coordinate delta since last move event.
            if (!isDragging) return;
            if (e.movementY === 0 && e.movementX === 0) {
                return;
            }
            const currentValue = this.currentEndValue;
            const valueDelta = Math.fround(e.movementX * valueUnitPerPx);
            const newValue = Math.max(this.currentStartValue, Math.min(this.maxValue, currentValue + valueDelta));
            this.setState(undefined, newValue);
        };

        /** @param {PointerEvent} e */
        const handlePointerDown = (e) => {
            dragStartValue = this.currentEndValue;
            valueUnitPerPx = this.computeValueUnitPerPx();
            control.setPointerCapture(e.pointerId);
            control.addEventListener("pointerup", handlePointerUp, false);
            control.addEventListener("pointermove", handlePointerMove, false);
            isDragging = true;
        };

        control.addEventListener("pointerdown", handlePointerDown, false);
        control.ondragstart = () => false;

        return () => {
            control.removeEventListener("pointerdown", handlePointerDown, false);
            control.removeEventListener("pointerup", handlePointerUp, false);
            control.removeEventListener("pointermove", handlePointerMove, false);
            control.ondragstart = null;
        };
    }

    setState(startValue, endValue) {
        let startProgress;
        let endProgress;
        if (startValue !== undefined) {
            this.currentStartValue = startValue;
            startProgress = (startValue - this.minValue) / this.totalRange;
            this.setControlPosition(this.elements.controlStart, startProgress);
        }
        if (endValue !== undefined) {
            this.currentEndValue = endValue;
            endProgress = 1 - ((endValue - this.minValue) / this.totalRange);
            this.setControlPosition(this.elements.controlEnd, endProgress);
        }
        if (this.currentStartValue === this.currentEndValue && this.currentEndValue === this.maxValue) {
            this.classList.add("start-z-2");
        } else {
            this.classList.remove("start-z-2");
        }
        this.elements.slider.setAttribute("aria-valuetext", `from ${this.currentStartValue} to ${this.currentEndValue}`);
        this.elements.slider.setAttribute("aria-valuenow", `${this.currentStartValue},${this.currentEndValue}`);
        this.setActiveLinePosition(startProgress, endProgress);
    }

    setControlPosition(control, progress) {
        const prop = control.classList.contains("plug-control-start") ? "left" : "right";
        control.style[prop] = `calc(${progress * 100}% - ${this.controlWidth / 2}px)`;
    }

    setActiveLinePosition(progressStart, progressEnd) {
        if (progressStart !== undefined) {
            this.elements.activeLine.style.left = `${progressStart * 100}%`;
        }
        if (progressEnd !== undefined) {
            this.elements.activeLine.style.right = `${progressEnd * 100}%`;
        }
    }
}

if (!window.customElements.get("plug-range-slider")) {
    window.customElements.define("plug-range-slider", PlugRangeSlider);
}
